﻿import { RouteRecordRaw, Router } from 'vue-router'
import { ELocalStorageKey, ERouterName } from '/@/types/enums'

const pageModules = import.meta.glob('../pages/page-web/**/*.vue')

const WorkspaceLayout = () => import('/@/pages/page-web/projects/workspace.vue')
const NewHomeLayout = () => import('/@/pages/page-web/newHome.vue')
const HomeLayout = () => import('/@/pages/page-web/home.vue')
const RouterViewLayout = () => import('/@/pages/page-web/routerViewLayout.vue')
const AlgorithmPlan = () => import('/@/components/algorithm/index.vue')
const AlarmCenter = () => import('/@/components/algorithm/center.vue')
const Binding = () => import('/@/components/algorithm/binding.vue')
const PushLog = () => import('/@/components/algorithm/pushLog.vue')
const Model = () => import('/@/pages/page-web/system/algorithm/model.vue')
const CreatePlan = () => import('/@/components/task/CreatePlan.vue')
const WaylinePanel = () => import('/@/pages/page-web/projects/wayline.vue')
const DockPanel = () => import('/@/pages/page-web/projects/dock.vue')
const AlgorithmModel = () => import('/@/pages/page-web/projects/algorithmModel.vue')
const IncidentPandling = () => import('/@/pages/page-web/projects/incidentPandling.vue')

export type RoleHomeConfig = {
  homeTitle?: string
  home_title?: string
  homeRoutePath?: string
  home_route_path?: string
  homeComponent?: string
  home_component?: string
  isMenuRoute?: number | string
  is_menu_route?: number | string
  enabled?: number | string
}

export function readStoredHomeConfig () {
  try {
    return JSON.parse(localStorage.getItem(ELocalStorageKey.HomeConfig) || 'null') as RoleHomeConfig | null
  } catch (err) {
    console.error('解析 home_config 失败:', err)
    return null
  }
}

export function resolveHomeConfigPath (defaultPath: string) {
  const homeConfig = readStoredHomeConfig()
  if (!homeConfig || Number(homeConfig.enabled) !== 1) {
    return defaultPath
  }
  const routePath = normalizeMenuPath(homeConfig.homeRoutePath || homeConfig.home_route_path || '')
  return routePath || defaultPath
}
export type BackendMenuNode = {
  id?: string | number
  name?: string
  path?: string
  component?: string
  hidden?: boolean
  perms?: string
  perms_type?: number
  redirect?: string | null
  always_show?: boolean
  dksl_icon?: string
  meta?: {
    title?: string
    icon?: string
    link?: string
    no_cache?: boolean
  }
  children?: BackendMenuNode[]
  [key: string]: any
}

export type RuntimeTopMenuItem = {
  type: 'link' | 'dropdown'
  key: string
  label: string
  to?: string
  targets: string[]
  children?: Array<{
    key: string
    label: string
    to: string
    targets: string[]
  }>
}

export type RuntimeSideMenuOption = {
  key: string
  label: string
  title: string
  path: string
  icon: string
}

const workspaceExtraChildren: BackendMenuNode[] = [
  {
    always_show: true,
    component: 'projects/waylinePlan',
    hidden: false,
    icon: '',
    meta: { title: '航点航线', icon: '', no_cache: true, link: '/waylinePlanB' },
    name: 'waylinePlanB',
    path: '/waylinePlanB',
    perms_type: 15,
    parent_id: '1865930343211626497',
    id: '111',
    order_num: 13,
    redirect: 'noRedirect'
  },
  {
    always_show: true,
    component: 'projects/obliqueWayline/index',
    hidden: false,
    icon: '',
    meta: { title: '倾斜航线', icon: '', no_cache: true, link: '/oblique' },
    name: 'oblique',
    path: '/oblique',
    perms_type: 15,
    parent_id: '1865930343211626498',
    id: '112',
    order_num: 13,
    redirect: 'noRedirect'
  },
  {
    always_show: true,
    component: 'projects/planarWayline/index',
    hidden: false,
    icon: '',
    meta: { title: '正射航线', icon: '', no_cache: true, link: '/planar' },
    name: 'planar',
    path: '/planar',
    perms_type: 15,
    parent_id: '1865930343211626499',
    id: '113',
    order_num: 14,
    redirect: 'noRedirect'
  }
]

export function isExternalPath (path?: string) {
  return /^https?:\/\//i.test(String(path || '').trim())
}

export function normalizeMenuPath (path?: string) {
  const value = String(path || '').trim()
  if (!value) return ''
  if (isExternalPath(value)) return value
  return value.startsWith('/') ? value : `/${value}`
}

export function normalizeManageSystemComparablePath (path?: string) {
  return normalizeMenuPath(path).replace(/^\/new_home(?=\/manage_[^/]+)/, '')
}

export function readStoredMenuList () {
  try {
    return JSON.parse(localStorage.getItem(ELocalStorageKey.MenuList) || '[]') as BackendMenuNode[]
  } catch (err) {
    console.error('解析 menu_list 失败:', err)
    return []
  }
}

export function cloneMenuList (menuList: BackendMenuNode[]) {
  return JSON.parse(JSON.stringify(menuList || [])) as BackendMenuNode[]
}

export function hasRegisteredRoutePath (router: Router, path: string) {
  const targetPath = normalizeMenuPath(path)
  if (!targetPath || isExternalPath(targetPath)) return false
  return router.getRoutes().some(route => normalizeMenuPath(route.path) === targetPath)
}

export function resolveMenuTarget (node: BackendMenuNode) {
  return normalizeMenuPath(node.path || node.meta?.link || node.name || '')
}

export function resolveMenuRouteNode (node: BackendMenuNode | null | undefined) {
  if (!node) return null
  const layoutChild = node.children?.find(child => child.component === 'Layout')
  return layoutChild || node
}

export function collectMenuTargets (node: BackendMenuNode | null | undefined): string[] {
  if (!node) return []
  const currentTarget = resolveMenuTarget(node)
  const childTargets = (node.children || []).flatMap(child => collectMenuTargets(child))
  return [currentTarget, ...childTargets].filter(Boolean)
}

export function findMenuNodeByPath (menus: BackendMenuNode[], targetPath: string): BackendMenuNode | null {
  const normalizedTarget = normalizeMenuPath(targetPath)
  for (const item of menus || []) {
    if (normalizeMenuPath(item.path) === normalizedTarget) {
      return item
    }
    if (item.children?.length) {
      const matched = findMenuNodeByPath(item.children, normalizedTarget)
      if (matched) return matched
    }
  }
  return null
}

export function findMenuNode (menus: BackendMenuNode[], predicate: (node: BackendMenuNode) => boolean): BackendMenuNode | null {
  for (const item of menus || []) {
    if (predicate(item)) {
      return item
    }
    if (item.children?.length) {
      const matched = findMenuNode(item.children, predicate)
      if (matched) return matched
    }
  }
  return null
}

export function getExplicitSystemMenuNode (menuList: BackendMenuNode[]) {
  return resolveMenuRouteNode(findMenuNode(menuList, item =>
    item.name === ERouterName.NEW_HOME || normalizeMenuPath(item.path) === `/${ERouterName.NEW_HOME}`
  ))
}

export function isManageSystemRoutePath (path: string) {
  return /^\/manage_[^/]+(?:\/.*)?$/.test(normalizeManageSystemComparablePath(path))
}

export function findMenuChainByPath (menus: BackendMenuNode[], targetPath: string, parents: BackendMenuNode[] = []): BackendMenuNode[] | null {
  const normalizedTarget = normalizeMenuPath(targetPath)
  for (const item of menus || []) {
    const currentChain = [...parents, item]
    if (normalizeMenuPath(item.path) === normalizedTarget) {
      return currentChain
    }
    if (item.children?.length) {
      const matched = findMenuChainByPath(item.children, normalizedTarget, currentChain)
      if (matched) return matched
    }
  }
  return null
}

export function isRouteInMenuNode (node: BackendMenuNode, currentPath: string): boolean {
  const nodePath = normalizeMenuPath(node.path)
  if (nodePath && (currentPath === nodePath || currentPath.startsWith(`${nodePath}/`))) {
    return true
  }
  return node.children?.some(child => isRouteInMenuNode(child, currentPath)) ?? false
}

export function getCurrentSystemMenuNode (menuList: BackendMenuNode[], currentPath: string) {
  const normalizedCurrentPath = normalizeMenuPath(currentPath)
  const explicitSystemNode = getExplicitSystemMenuNode(menuList)
  const isManageSystemRoute = isManageSystemRoutePath(normalizedCurrentPath)

  if (normalizedCurrentPath === `/${ERouterName.NEW_HOME}`) {
    return resolveMenuRouteNode(explicitSystemNode)
  }

  if (isManageSystemRoute) {
    return resolveMenuRouteNode(explicitSystemNode)
  }

  if (explicitSystemNode && isRouteInMenuNode(explicitSystemNode, normalizedCurrentPath)) {
    return resolveMenuRouteNode(explicitSystemNode)
  }

  return null
}

export function buildSystemMenuOptions (node: BackendMenuNode | null, hiddenPaths: string[] = []) {
  const hiddenSet = new Set(hiddenPaths.map(item => normalizeMenuPath(item)))
  return (node?.children || [])
    .filter(child => {
      const childPath = normalizeMenuPath(child.path)
      return !child.hidden && !hiddenSet.has(childPath) && isManageSystemRoutePath(childPath)
    })
    .map((child) => ({
      key: String(child.id || child.path || child.name || ''),
      label: child.meta?.title || child.name || '',
      title: child.meta?.title || child.name || '',
      path: normalizeMenuPath(child.path),
      icon: child.meta?.icon || child.dksl_icon || ''
    }))
    .filter(item => !!item.label && !!item.path) as RuntimeSideMenuOption[]
}

function buildTopMenuChildren (children: BackendMenuNode[]) {
  return children
    .filter(child => !child.hidden)
    .map(child => {
      const label = child.meta?.title || child.name || ''
      const target = resolveMenuTarget(child)
      if (!label || !target) return null
      return {
        key: String(child.name || child.path || child.id || target),
        label,
        to: target,
        targets: [target]
      }
    })
    .filter(Boolean) as NonNullable<RuntimeTopMenuItem['children']>
}

export function buildTopMenuItems (menuList: BackendMenuNode[]) {
  const items: RuntimeTopMenuItem[] = []
  const navigationNode = findMenuNode(menuList, item =>
    item.name === 'navigation' || normalizeMenuPath(item.path) === '/navigation'
  )
  const systemSettingNode = findMenuNode(menuList, item =>
    item.name === ERouterName.NEW_HOME || normalizeMenuPath(item.path) === `/${ERouterName.NEW_HOME}`
  )
  const systemSettingRouteNode = resolveMenuRouteNode(systemSettingNode)
  const hasVisibleNavigationChildren = Array.isArray(navigationNode?.children) &&
    navigationNode.children.some(child => !child.hidden)

  if (!hasVisibleNavigationChildren) {
    return []
  }

  if (navigationNode?.children?.length) {
    navigationNode.children
      .filter(child => !child.hidden)
      .forEach(node => {
        const label = node.meta?.title || node.name || ''
        if (!label) return
        const key = String(node.name || node.path || node.id || label)
        const rawTarget = resolveMenuTarget(node)
        const isSystemSettingsEntry =
          rawTarget === `/${ERouterName.NEW_HOME}` ||
          node.name === ERouterName.NEW_HOME ||
          normalizeMenuPath(node.path) === `/${ERouterName.NEW_HOME}`
        if (isSystemSettingsEntry && systemSettingRouteNode) {
          const systemChildren = buildSystemMenuOptions(systemSettingRouteNode)
          const firstChildTarget = systemChildren[0]?.path ||
            systemSettingRouteNode.children
              ?.map(child => resolveMenuTarget(child))
              .find(Boolean)
          const target = firstChildTarget || resolveMenuTarget(systemSettingRouteNode)
          if (!target) return
          items.push({
            type: 'link',
            key,
            label,
            to: target,
            targets: collectMenuTargets(systemSettingRouteNode)
          })
          return
        }
        const children = buildTopMenuChildren(node.children || [])
        if (children.length) {
          items.push({
            type: 'dropdown',
            key,
            label,
            targets: children.flatMap(child => child.targets),
            children
          })
          return
        }
        let target = rawTarget
        let targets = collectMenuTargets(node)
        if (!target) return
        items.push({
          type: 'link',
          key,
          label,
          to: target,
          targets
        })
      })
  }

  return items
}

export function resolveDynamicRouteComponent (routePath?: string, component?: string) {
  const normalizedPath = normalizeMenuPath(routePath)
  if (normalizedPath === '/task') return null
  if (normalizedPath === '/create-plan') return CreatePlan
  if (normalizedPath === '/wayline-panel') return WaylinePanel
  if (normalizedPath === '/route_management') return WaylinePanel
  if (normalizedPath === '/dock-panel') return DockPanel
  if (normalizedPath === '/model') return Model
  if (normalizedPath === '/binding') return Binding
  if (normalizedPath === '/pushLog') return PushLog
  if (normalizedPath === '/center' || component === 'alarm/center') return AlarmCenter
  if (normalizedPath === '/algorithm_model') return AlgorithmModel
  if (normalizedPath === '/incident_pandling') return IncidentPandling
  if (!component) return null
  return pageModules[`../pages/page-web/${component}.vue`] || null
}

export function resolveDynamicLayoutComponent (path: string, level: number) {
  if (level > 0) {
    return RouterViewLayout
  }
  if (path === '/home') {
    return HomeLayout
  }
  if (path === '/new_home') {
    return NewHomeLayout
  }
  return WorkspaceLayout
}

export function enhanceBackendMenuList (menuList: BackendMenuNode[]) {
  const clonedMenu = cloneMenuList(menuList)
  clonedMenu.forEach(route => {
    if (normalizeMenuPath(route.path) !== '/workspace') {
      return
    }
    route.children = route.children || []
    workspaceExtraChildren.forEach(extraChild => {
      const exists = route.children?.some(child => normalizeMenuPath(child.path) === normalizeMenuPath(extraChild.path))
      if (!exists) {
        route.children?.push({ ...extraChild })
      }
    })
  })
  return clonedMenu
}

export function buildAsyncRoutes (routes: BackendMenuNode[], level = 0): RouteRecordRaw[] {
  const asyncRoutes: RouteRecordRaw[] = []
  routes.forEach((route) => {
    const tmpRoute = { ...route } as RouteRecordRaw
    const hasChildren = Array.isArray(route.children) && route.children.length > 0

    if ((route as BackendMenuNode).perms_type === 16) {
      return
    }

    if (normalizeMenuPath(String(tmpRoute.path || '')) === '/task') {
      return
    }

    if (isExternalPath(String(tmpRoute.path || ''))) {
      return
    }

    tmpRoute.redirect = null
    if (tmpRoute.perms_type === 15 && !hasChildren) {
      delete tmpRoute.children
    }

    if (tmpRoute.component?.toString() === 'Layout') {
      if (tmpRoute.path === '/algorithm' && level === 0) {
        const algorithmChildren = route.children ? buildAsyncRoutes(route.children, level + 1) : []
        const algorithmTabPaths = ['/center', '/binding', '/model', '/pushLog']
        const algorithmTabChildren = algorithmChildren.filter((child: any) => algorithmTabPaths.includes(String(child.path)))
        const algorithmOtherChildren = algorithmChildren.filter((child: any) => !algorithmTabPaths.includes(String(child.path)))
        tmpRoute.component = resolveDynamicLayoutComponent(String(tmpRoute.path || ''), level)
        tmpRoute.redirect = '/center'
        tmpRoute.children = []
        if (algorithmTabChildren.length > 0) {
          tmpRoute.children.push({
            path: '',
            name: `${String(route.name || 'algorithm')}_container`,
            component: AlgorithmPlan,
            children: algorithmTabChildren
          } as any)
        }
        if (algorithmOtherChildren.length > 0) {
          tmpRoute.children.push(...algorithmOtherChildren as any)
        }
        asyncRoutes.push(tmpRoute)
        return
      }
      tmpRoute.component = resolveDynamicLayoutComponent(String(tmpRoute.path || ''), level)
    } else if (tmpRoute.component) {
      const resolvedComponent = resolveDynamicRouteComponent(String(tmpRoute.path || ''), String(tmpRoute.component))
      if (resolvedComponent) {
        tmpRoute.component = resolvedComponent as any
      } else {
        delete tmpRoute.component
      }
    }

    if (tmpRoute.children) {
      tmpRoute.children = buildAsyncRoutes(route.children as BackendMenuNode[], level + 1)
    }

    const hasValidChildren = Array.isArray(tmpRoute.children) && tmpRoute.children.length > 0
    const hasRouteComponent = typeof tmpRoute.component === 'function' || (typeof tmpRoute.component === 'object' && tmpRoute.component !== null)

    if (!hasRouteComponent && hasValidChildren) {
      tmpRoute.component = resolveDynamicLayoutComponent(String(tmpRoute.path || ''), level)
    }

    if (!hasRouteComponent && !hasValidChildren) {
      return
    }

    asyncRoutes.push(tmpRoute)
  })
  return asyncRoutes
}
